/**
 * The drying graph's compact axes, per screen size. The extractor draws the axes with 18 px tick
 * labels, up to 12 x-axis labels, and the y-axis titles ("Temp (°C)", "rel. Feuchte (%)") as each
 * axis's top tick, which makes both y axes as wide as their title. On a phone that left the plot
 * about two thirds of the width, and on desktop too the titles cost the plot (and the RENO strip
 * aligned under it) width for nothing (Taimoor, 2026-10-06).
 *
 * A profile shrinks the tick font and keeps the x axis to fewer labels where it sets them, tightens
 * the tick padding, drops the tick marks, and takes the titles out of the tick labels: the screen
 * shows them above the plot instead (`getGraphAxisTitles`), so each y axis is only as wide as its
 * numbers. It also sets the plot's height, lower below desktop.
 */
/** Space between a y axis's numbers and the plot, in px. */
export const GRAPH_Y_TICK_PADDING = 8;
/**
 * Space between the x-axis dates and the lowest y-axis number, in px. The lowest number ("0") is
 * centred on the plot's bottom edge, so half of it hangs below the plot: the dates sit that half plus
 * this gap below the edge, or they touch the number (Taimoor, 2026-10-07).
 */
export const GRAPH_X_TICK_GAP = 4;
/** Chart.js's tick font size when an axis sets none. */
const CHART_DEFAULT_TICK_FONT_SIZE = 12;

/** Key prefix of the phase bands (P2, P3, …) among the graph's annotations; the extractor names them with it. */
export const PHASE_ANNOTATION_KEY_PREFIX = "phaseLines_";

/** How far a screen size shrinks the axes. */
export interface GraphSizeProfile {
  /** Tick label size in px; null keeps the extractor's. */
  tickFontSize: number | null;
  /** At most this many x-axis labels; null keeps the extractor's limit. */
  maxXTicks: number | null;
  /** The y-axis titles leave the ticks for a line above the plot. */
  titlesAbovePlot: boolean;
  /** The plot's height in px. */
  height: number;
  /** Phase label (P2, P3, …) size in px; null keeps the chart's default 12 px. */
  phaseLabelFontSize: number | null;
}

/**
 * Phones (below 600 px): 240 px high, a 400 px plot was taller than a phone is wide. The phase labels
 * are bold, so a step below the 11 px numbers reads as their size (Taimoor, 2026-10-06).
 */
export const PHONE_GRAPH_PROFILE: GraphSizeProfile = {
  tickFontSize: 11,
  maxXTicks: 3,
  titlesAbovePlot: true,
  height: 240,
  phaseLabelFontSize: 10,
};

/** iPad mini and portrait iPads (600-959 px): like the phone, a step larger. */
export const NARROW_TABLET_GRAPH_PROFILE: GraphSizeProfile = {
  tickFontSize: 13,
  maxXTicks: 5,
  titlesAbovePlot: true,
  height: 280,
  phaseLabelFontSize: null,
};

/** iPad Pro and landscape iPads (960-1263 px): smaller ticks and fewer dates. */
export const WIDE_TABLET_GRAPH_PROFILE: GraphSizeProfile = {
  tickFontSize: 14,
  maxXTicks: 8,
  titlesAbovePlot: true,
  height: 320,
  phaseLabelFontSize: null,
};

/**
 * Desktop (1264 px up): the extractor's ticks, only the titles above the plot. The chart's own 400 px
 * height is set explicitly: without it the chart keeps whatever height it had, so a window widened from
 * phone or tablet size kept the lower graph (Taimoor, 2026-10-07).
 */
export const DESKTOP_GRAPH_PROFILE: GraphSizeProfile = {
  tickFontSize: null,
  maxXTicks: null,
  titlesAbovePlot: true,
  height: 400,
  phaseLabelFontSize: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TickCallback = (this: any, value: number | string, index: number, ticks: any[]) => any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GraphScale = { axis?: string; position?: string; max?: number; ticks?: Record<string, any>; grid?: Record<string, any>; [key: string]: any };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GraphOptions = { scales?: Record<string, GraphScale>; [key: string]: any };

/** A y axis's title as the header above the plot shows it, in the colour and size of its tick labels. */
export interface GraphAxisTitle {
  text: string;
  color: string | null;
  /** The axis's tick font size in px, so the title matches its numbers; null when the axis sets none. */
  fontSize: number | null;
}

/** Per side, in axis order: a ComBox with resistance sensors has two right axes. */
export interface GraphAxisTitles {
  left: GraphAxisTitle[];
  right: GraphAxisTitle[];
}

/** The y axis's own label for a tick, without the leading spaces the desktop layout adds. */
function trimLabel(label: unknown): unknown {
  if (Array.isArray(label)) {
    return label.map((line) => (typeof line === "string" ? line.trim() : line));
  }
  return typeof label === "string" ? label.trim() : label;
}

/**
 * The y-axis titles, read from each axis's own tick callback at the axis maximum: that tick is the
 * title (an array of lines, e.g. ["Temp", "(°C)"]), whatever weather or power options shaped it.
 */
export function getGraphAxisTitles(options: GraphOptions | null | undefined): GraphAxisTitles {
  const titles: GraphAxisTitles = { left: [], right: [] };

  Object.values(options?.scales ?? {}).forEach((scale) => {
    const callback = scale?.ticks?.callback as TickCallback | undefined;
    if (!scale || scale.axis !== "y" || scale.display === false || typeof callback !== "function" || typeof scale.max !== "number") {
      return;
    }

    const label = trimLabel(callback.call(scale, scale.max, 0, []));
    if (!Array.isArray(label)) {
      return;
    }

    const title = {
      text: label.filter((line) => !!line).join(" "),
      color: scale.ticks?.color ?? null,
      fontSize: scale.ticks?.font?.size ?? null,
    };
    (scale.position === "right" ? titles.right : titles.left).push(title);
  });

  return titles;
}

/** The annotation plugin's options with the phase labels at `fontSize`; the input is left untouched. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withPhaseLabelFontSize(plugins: Record<string, any> | undefined, fontSize: number | null) {
  const annotations = plugins?.annotation?.annotations;
  if (fontSize === null || !annotations) {
    return plugins;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resized: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Object.entries(annotations as Record<string, any>).forEach(([key, annotation]) => {
    resized[key] =
      key.startsWith(PHASE_ANNOTATION_KEY_PREFIX) && annotation?.label
        ? { ...annotation, label: { ...annotation.label, font: { ...(annotation.label.font ?? {}), size: fontSize } } }
        : annotation;
  });

  return { ...plugins, annotation: { ...plugins?.annotation, annotations: resized } };
}

/** Vuetify's breakpoint flags a profile is chosen by. */
export interface GraphSizeBreakpoint {
  xsOnly: boolean;
  smOnly: boolean;
  mdOnly: boolean;
}

/**
 * The graph's profile for a screen width: phones, iPad mini and portrait iPads (600-959 px), iPad Pro and
 * landscape iPads (960-1263 px), desktop. One rule for every screen that embeds the drying graph (the
 * device detail, the RENO review).
 */
export function graphSizeProfileFor(breakpoint: GraphSizeBreakpoint): GraphSizeProfile {
  if (breakpoint.xsOnly) {
    return PHONE_GRAPH_PROFILE;
  }

  if (breakpoint.smOnly) {
    return NARROW_TABLET_GRAPH_PROFILE;
  }

  return breakpoint.mdOnly ? WIDE_TABLET_GRAPH_PROFILE : DESKTOP_GRAPH_PROFILE;
}

/** Returns a copy of `options` with the profile's axes and phase labels; the input is left untouched. */
export function applyCompactGraphOptions<T extends GraphOptions>(options: T, profile: GraphSizeProfile): T {
  if (!options || !options.scales) {
    return options;
  }

  const scales: Record<string, GraphScale> = {};

  Object.entries(options.scales).forEach(([id, scale]) => {
    if (!scale || !scale.ticks) {
      scales[id] = scale;
      return;
    }

    const isX = scale.axis === "x";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ticks: Record<string, any> = { ...scale.ticks };

    if (profile.tickFontSize !== null) {
      ticks.font = { ...(scale.ticks.font ?? {}), size: profile.tickFontSize };
    }

    if (isX && profile.maxXTicks !== null && typeof scale.ticks.maxTicksLimit === "number") {
      ticks.maxTicksLimit = Math.min(scale.ticks.maxTicksLimit, profile.maxXTicks);
    }

    if (!profile.titlesAbovePlot) {
      scales[id] = { ...scale, ticks };
      return;
    }

    const fontSize = ticks.font?.size ?? CHART_DEFAULT_TICK_FONT_SIZE;
    ticks.padding = isX ? Math.ceil(fontSize / 2) + GRAPH_X_TICK_GAP : GRAPH_Y_TICK_PADDING;

    const callback = scale.ticks.callback as TickCallback | undefined;
    if (!isX && typeof callback === "function") {
      // The title tick (an array of lines) moves above the plot; the numbers lose their padding spaces.
      ticks.callback = function (this: unknown, value: number | string, index: number, allTicks: unknown[]) {
        const label = trimLabel(callback.call(this, value, index, allTicks as never[]));
        return Array.isArray(label) ? "" : label;
      };
    }

    scales[id] = { ...scale, ticks, grid: { ...(scale.grid ?? {}), tickLength: 0 } };
  });

  const plugins = withPhaseLabelFontSize(options.plugins, profile.phaseLabelFontSize);
  return plugins === options.plugins ? { ...options, scales } : { ...options, scales, plugins };
}
